import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";
import ErrorBoundary from "@/components/ErrorBoundary";
import CapacitorBootstrap from "@/components/CapacitorBootstrap";

import Landing from "@/pages/Landing";
import { isNativeApp } from "@/lib/platform";

// Native shell (Capacitor) skips the marketing landing page and opens
// straight to /login. The web build keeps the full landing experience.
function RootRoute() {
  return isNativeApp() ? <Navigate to="/login" replace /> : <Landing />;
}
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LegalPage from "@/pages/LegalPage";
import PublicSupportPage from "@/pages/SupportPage";
import PaymentInstructionsPage from "@/pages/PaymentInstructionsPage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SubAdminsPage from "@/pages/admin/SubAdminsPage";
import PendingStudentsPage from "@/pages/admin/PendingStudentsPage";
import ApprovalsInboxPage from "@/pages/admin/ApprovalsInboxPage";
import StudentListPage from "@/pages/admin/StudentListPage";
import CoursesPage from "@/pages/admin/CoursesPage";
import SubjectsPage from "@/pages/admin/SubjectsPage";
import ModulesPage from "@/pages/admin/ModulesPage";
import QuestionsPage from "@/pages/admin/QuestionsPage";
import SupportPage from "@/pages/admin/SupportPage";
import AbstractExamRepositoryPage from "@/pages/admin/AbstractExamRepositoryPage";
import StudentAbstractExam from "@/pages/student/StudentAbstractExam";
import SettingsPage from "@/pages/admin/SettingsPage";
import LearningMonitorPage from "@/pages/admin/LearningMonitorPage";
import PushBroadcastPage from "@/pages/admin/PushBroadcastPage";
import AdminAiConfig from "@/pages/admin/AdminAiConfig";
import AdminShareAnalyticsPage from "@/pages/admin/AdminShareAnalyticsPage";
import EnrollmentRequestsPage from "@/pages/admin/EnrollmentRequestsPage";
import PaymentsPage from "@/pages/admin/PaymentsPage";
import AccountingPage from "@/pages/admin/AccountingPage";
import TemporaryAccessStudentsPage from "@/pages/admin/TemporaryAccessStudentsPage";

import StudentLayout from "@/pages/student/StudentLayout";
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentHome from "@/pages/student/StudentHome";
import StudentAccount from "@/pages/student/StudentAccount";
import StudentCourses from "@/pages/student/StudentCourses";
import StudentSubjects from "@/pages/student/StudentSubjects";
import StudentModules from "@/pages/student/StudentModules";
import StudentReview from "@/pages/student/StudentReview";
import StudentReviewMode from "@/pages/student/StudentReviewMode";
import StudentModulePodcast from "@/pages/student/StudentModulePodcast";
import StudentSupport from "@/pages/student/StudentSupport";
import SmartReviewPage from "@/pages/student/SmartReviewPage";
import SmartReviewSession from "@/pages/student/SmartReviewSession";
import StudyHub from "@/pages/student/StudyHub";
import MockExamHome from "@/pages/student/MockExamHome";
import MockExamRunner from "@/pages/student/MockExamRunner";
import MockExamResults from "@/pages/student/MockExamResults";
import ProgressCard from "@/pages/student/ProgressCard";
import PublicProgressCard from "@/pages/PublicProgressCard";
import PublicShare from "@/pages/PublicShare";
import StudentLeaderboard from "@/pages/student/StudentLeaderboard";
import StudentBadges from "@/pages/student/StudentBadges";
import StudentAnalytics from "@/pages/student/StudentAnalytics";
import StudentEnrollments from "@/pages/student/StudentEnrollments";
import StudentProfile from "@/pages/student/StudentProfile";
import StudentProfileComplete from "@/pages/student/StudentProfileComplete";
import SmartReviewOffline from "@/pages/student/SmartReviewOffline";
import AuthCallback from "@/pages/AuthCallback";
import FriendsHome from "@/pages/student/FriendsHome";
import MyQRPage from "@/pages/student/MyQRPage";
import ScanQRPage from "@/pages/student/ScanQRPage";
import SocialSetupPage from "@/pages/student/SocialSetupPage";
import CommunityGuidelines from "@/pages/legal/CommunityGuidelines";
import AdminReportsQueue from "@/pages/admin/AdminReportsQueue";
import FeedPage from "@/pages/student/FeedPage";
import StudentSettings from "@/pages/student/StudentSettings";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
      <BrowserRouter>
        <CapacitorBootstrap />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/legal/:tab" element={<LegalPage />} />
          <Route path="/support" element={<PublicSupportPage />} />
          <Route path="/help" element={<Navigate to="/support" replace />} />
          <Route path="/faq" element={<Navigate to="/support" replace />} />
          <Route path="/legal/community-guidelines" element={<CommunityGuidelines />} />
          {/* Short URLs for Google OAuth verification (must be reachable at the top-level path) */}
          <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/payment-instructions" element={<PaymentInstructionsPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/share/p/:token" element={<PublicProgressCard />} />
          <Route path="/share/badge/:token" element={<PublicShare kind="badge" />} />
          <Route path="/share/exam/:token" element={<PublicShare kind="exam_result" />} />
          <Route path="/share/mock-exam/:token" element={<PublicShare kind="mock_exam" />} />
          <Route path="/share/streak/:token" element={<PublicShare kind="streak" />} />

          <Route path="/admin" element={<Protected roles={["super_admin", "sub_admin", "moderator"]}><AdminLayout /></Protected>}>
            <Route index element={<AdminDashboard />} />
            <Route path="sub-admins" element={<SubAdminsPage />} />
            <Route path="pending" element={<PendingStudentsPage />} />
            <Route path="approvals" element={<ApprovalsInboxPage />} />
            <Route path="students" element={<StudentListPage />} />
            <Route path="temporary-access" element={<TemporaryAccessStudentsPage />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="courses/:courseId/subjects" element={<SubjectsPage />} />
            <Route path="subjects/:subjectId/modules" element={<ModulesPage />} />
            <Route path="modules/:moduleId/questions" element={<QuestionsPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="learning-monitor" element={<LearningMonitorPage />} />
            <Route path="broadcasts" element={<PushBroadcastPage />} />
            <Route path="share-analytics" element={<AdminShareAnalyticsPage />} />
            <Route path="enrollment-requests" element={<EnrollmentRequestsPage />} />
            <Route path="reports" element={<AdminReportsQueue />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="accounting" element={<AccountingPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="ai-config" element={<AdminAiConfig />} />
            <Route path="abstract-exam" element={<AbstractExamRepositoryPage />} />
          </Route>

          <Route path="/student" element={<Protected roles={["student"]}><StudentLayout /></Protected>}>
            <Route index element={<StudentHome />} />
            <Route path="account" element={<StudentAccount />} />
            <Route path="courses" element={<StudentCourses />} />
            <Route path="courses/:courseId/subjects" element={<StudentSubjects />} />
            <Route path="subjects/:subjectId/modules" element={<StudentModules />} />
            <Route path="modules/:moduleId/review" element={<StudentReview />} />
            <Route path="modules/:moduleId/review-mode" element={<StudentReviewMode />} />
            <Route path="modules/:moduleId/podcast" element={<StudentModulePodcast />} />
            <Route path="study" element={<StudyHub />} />
            <Route path="smart-review" element={<SmartReviewPage />} />
            <Route path="smart-review/session" element={<SmartReviewSession />} />
            <Route path="smart-review/offline" element={<SmartReviewOffline />} />
            <Route path="mock-exam" element={<MockExamHome />} />
            <Route path="mock-exam/:sessionId" element={<MockExamRunner />} />
            <Route path="mock-exam/:sessionId/results" element={<MockExamResults />} />
            <Route path="friends" element={<FriendsHome />} />
            <Route path="friends/setup" element={<SocialSetupPage />} />
            <Route path="friends/qr" element={<MyQRPage />} />
            <Route path="friends/scan" element={<ScanQRPage />} />
            <Route path="feed" element={<FeedPage />} />
            <Route path="abstract-exam" element={<StudentAbstractExam />} />
            <Route path="leaderboard" element={<StudentLeaderboard />} />
            <Route path="badges" element={<StudentBadges />} />
            <Route path="analytics" element={<StudentAnalytics />} />
            <Route path="enrollments" element={<StudentEnrollments />} />
            <Route path="profile" element={<StudentProfile />} />
            <Route path="settings" element={<StudentSettings />} />
            <Route path="profile/complete" element={<StudentProfileComplete />} />
            <Route path="progress-card" element={<ProgressCard />} />
            <Route path="support" element={<StudentSupport />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <InstallPWAPrompt />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
